// src/components/tracking/StopDetailsPanel.jsx
import React, { useMemo, useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { getRouteById } from "../../services/routeService";

export default function StopDetailsPanel({ stop, onClose, openBusDetails, realTimeEtas = [] }) {
    const { token } = useAuth();
    const [routeNames, setRouteNames] = useState({});

    // 🔥 1. PROCESAMIENTO DE ETAS 🔥
    const activeRoutes = useMemo(() => {
        if (!stop) return [];
        if (!Array.isArray(realTimeEtas) || realTimeEtas.length === 0) return [];

        const rawStopId = stop.stoId || stop.id || stop.stopId; 
        if (rawStopId == null) return [];
        
        const stopId = Number(rawStopId); 

        const etasForThisStop = realTimeEtas.filter(eta => Number(eta.stoId) === stopId);

        const routesMap = {};
        etasForThisStop.forEach(eta => {
            const routeId = eta.rouId || 'Desconocida';
            
            if (!routesMap[routeId]) {
                routesMap[routeId] = {
                    id: routeId,
                    name: routeNames[routeId] || `Cargando Ruta #${routeId}...`,
                    buses: []
                };
            }
            routesMap[routeId].buses.push({
                id: eta.busId,
                eta: `${eta.timeForNextStop} min`,
                rawTime: Number(eta.timeForNextStop) || 0, 
                direction: eta.isGoingForward ? 'Ida' : 'Vuelta'
            });
        });

        const groupedArray = Object.values(routesMap);
        groupedArray.forEach(route => {
            route.buses.sort((a, b) => a.rawTime - b.rawTime);
        });

        return groupedArray;
    }, [stop, realTimeEtas, routeNames]);

    // 🔥 2. EFECTO PARA CARGAR NOMBRES DE RUTA REALES 🔥
    useEffect(() => {
        if (!token || activeRoutes.length === 0) return;

        activeRoutes.forEach(async (route) => {
            if (!routeNames[route.id] && route.id !== 'Desconocida') {
                try {
                    const data = await getRouteById(token, route.id);
                    if (data && data.rouName) {
                        setRouteNames(prev => ({
                            ...prev,
                            [route.id]: data.rouName
                        }));
                    }
                } catch (error) {
                    console.error(`Error cargando nombre de ruta ${route.id}:`, error);
                    setRouteNames(prev => ({ ...prev, [route.id]: `Ruta ${route.id}` }));
                }
            }
        });
    }, [activeRoutes, token, routeNames]);

    // 🔥 3. CONDICIÓN DE SALIDA 🔥
    if (!stop) return null;

    const displayStopName = stop.name || stop.stopName || stop.stoName || "Parada sin nombre";

    return (
        <div style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            width: 'calc(100% - 32px)', 
            maxWidth: '320px',          
            maxHeight: 'calc(100% - 100px)', 
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
            zIndex: 90, 
            display: 'flex',
            flexDirection: 'column',
            animation: 'fadeIn 0.2s ease-out',
            border: '1px solid #E5E7EB',
            borderTop: '4px solid #EAC024' 
        }}>
            {/* --- HEADER (Título de la Parada) --- */}
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#FAFAFA', borderTopLeftRadius: '8px', borderTopRightRadius: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0 }}>
                    <div style={{ width: 32, height: 32, borderRadius: '50%', backgroundColor: '#FEF9C3', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#EAC024', flexShrink: 0 }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M12 22s-8-4.5-8-11.8A8 8 0 0 1 12 2a8 8 0 0 1 8 8.2c0 7.3-8 11.8-8 11.8z"></path>
                            <circle cx="12" cy="10" r="3"></circle>
                        </svg>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ margin: 0, fontSize: '10px', color: '#6B7280', textTransform: 'uppercase', fontWeight: 700 }}>Parada Seleccionada</p>
                        <h3 style={{ margin: 0, fontSize: '14px', color: '#1F2937', fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {displayStopName}
                        </h3>
                    </div>
                </div>
                <button onClick={onClose} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#9CA3AF', padding: '4px', transition: 'color 0.2s', flexShrink: 0, marginLeft: '8px' }} onMouseOver={e => e.currentTarget.style.color = '#EF4444'} onMouseOut={e => e.currentTarget.style.color = '#9CA3AF'}>✕</button>
            </div>

            {/* --- CUERPO SCROLLABLE (Lista de Rutas y ETAs) --- */}
            <div style={{ padding: '12px 16px', overflowY: 'auto', flex: 1 }}>
                
                {activeRoutes.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '20px 0', color: '#6B7280' }}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '0 auto 8px auto', color: '#9CA3AF' }}><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                        <p style={{ margin: 0, fontSize: '13px', fontWeight: 500 }}>No hay unidades en camino </p>
                    </div>
                ) : (
                    <>
                        <p style={{ margin: '0 0 12px 0', fontSize: '12px', color: '#4B5563', fontWeight: 600 }}>Próximas llegadas estimadas:</p>
                        
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {activeRoutes.map(route => (
                                <div key={route.id} style={{ border: '1px solid #E5E7EB', borderLeft: '3px solid #EAC024', borderRadius: '8px', backgroundColor: '#FFFFFF', boxShadow: '0 1px 2px rgba(0,0,0,0.02)', overflow: 'hidden' }}>
                                    <h4 style={{ margin: 0, padding: '10px 12px', fontSize: '12px', color: '#374151', borderBottom: '1px solid #E5E7EB', backgroundColor: '#FAFAFA', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{route.name}</h4>
                                    
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        {route.buses.map((bus, index) => (
                                            <div 
                                                key={bus.id} 
                                                onClick={(e) => { 
                                                    e.stopPropagation(); 
                                                    if (openBusDetails) openBusDetails(bus.id); 
                                                }}
                                                style={{ 
                                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                                    padding: '8px 12px',
                                                    borderBottom: index < route.buses.length - 1 ? '1px solid #F3F4F6' : 'none',
                                                    cursor: 'pointer', transition: 'background-color 0.2s'
                                                }}
                                                onMouseOver={e => e.currentTarget.style.backgroundColor = '#F9FAFB'}
                                                onMouseOut={e => e.currentTarget.style.backgroundColor = 'transparent'}
                                            >
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    {/* 🔥 CAMBIO AQUÍ: Todos los puntos son verdes siempre 🔥 */}
                                                    <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#10B981', flexShrink: 0 }}></div>
                                                    <span style={{ fontSize: '11px', color: '#4B5563', fontWeight: 600 }}>Bus #{bus.id}</span>
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <span style={{ fontSize: '11px', fontWeight: 700, color: '#D97706', backgroundColor: '#FEF3C7', padding: '4px 6px', borderRadius: '12px', flexShrink: 0 }}>
                                                        {bus.rawTime === 0 ? 'En la parada' : `Llega en ${bus.eta}`}
                                                    </span>
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <polyline points="9 18 15 12 9 6"></polyline>
                                                    </svg>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}